"use client";

import { useEffect, useEffectEvent } from "react";
import Cookies from "js-cookie";

export type LinkParamConfig = {
  /** CSS selector for the links that should get gtm_id appended, e.g. 'a[href*="intercom.com"]'. Only affects gtm_id. */
  selector: string;
  /** Explicit list of route URLs that should get utm_referrer appended. Matched by origin + pathname against any clicked link, independent of the selector. */
  utmRoutes?: string[];
  /**
   * Locale prefix (first path segment) to the `locale` the signup app should
   * open in. Applied to the same utmRoutes matches. A plain object, not a
   * resolver function — root-shell is a Server Component and cannot hand a
   * function across the client boundary.
   *
   * The signup app is a separate React app behind the CDN, so no locale
   * middleware runs on it and its URLs must never be path-prefixed — a prefix
   * would also stop them matching utmRoutes and drop signup attribution. A
   * query param is the only way to carry the locale across.
   */
  signupLocaleByPrefix?: Record<string, string>;
};

// utm_referrer forwards the current URL to the signup flow. On the BYOHC
// preview surface the URL carries a secret that must never travel cross-site:
// `token` directly, and `redirectTo` (the password gate embeds the tokenized
// URL inside it). Scope the strip to these routes — `token` is also a
// legitimate attribution param for DRLP personalization elsewhere, so stripping
// it globally would silently drop that data.
const SECRET_BEARING_ROUTES = ["/fin/demo/preview", "/auth"];
const SECRET_PARAMS = ["token", "redirectTo"];

// These routes also serve under a locale prefix (e.g. /ps/auth on previews),
// which this package can't enumerate, so a leading segment is also skipped.
function isSecretBearing(pathname: string): boolean {
  const unprefixed = pathname.replace(/^\/[^/]+/, "");
  return SECRET_BEARING_ROUTES.some((route) =>
    [pathname, unprefixed].some(
      (path) => path === route || path.startsWith(`${route}/`),
    ),
  );
}

function safeReferrer(): string {
  try {
    const here = new URL(window.location.href);
    if (isSecretBearing(here.pathname)) {
      SECRET_PARAMS.forEach((param) => here.searchParams.delete(param));
    }
    return here.toString();
  } catch {
    return window.location.href;
  }
}

export default function AddUrlParamsToCrossSiteLinks({
  selector,
  utmRoutes,
  signupLocaleByPrefix,
}: LinkParamConfig) {
  const decorateLink = useEffectEvent((target: Element | null) => {
    if (!target) return;

    const link: HTMLAnchorElement | null =
      target.closest(selector) ?? target.closest("a[href]");

    if (!link) return;

    try {
      const newUrl = new URL(link.href);
      let modified = false;

      // gtm_id: only added to links matching the CSS selector
      if (target.closest(selector)) {
        const gtmId = Cookies.get("gtm_id");
        if (gtmId) {
          newUrl.searchParams.set("gtm_id", gtmId);
          modified = true;
        }
      }

      // utm_referrer: added to any link whose origin + pathname matches a utmRoute, regardless of selector
      if (utmRoutes?.length && window?.location?.href) {
        const shouldIncludeUtm = utmRoutes.some((route) => {
          try {
            const routeUrl = new URL(route);
            return (
              routeUrl.origin === newUrl.origin &&
              routeUrl.pathname === newUrl.pathname
            );
          } catch {
            // Relative entry (e.g. "/start-free-trial"): match an internal
            // same-site route by pathname. The getAttribute guard avoids
            // matching a cross-origin link that merely shares the pathname.
            const rawHref = link.getAttribute("href") ?? "";
            return rawHref.startsWith("/") && newUrl.pathname === route;
          }
        });

        if (shouldIncludeUtm) {
          newUrl.searchParams.set("utm_referrer", safeReferrer());
          modified = true;
        }

        // Never overwrite an authored locale: the DRLP route dropdown pins one per CTA.
        if (
          shouldIncludeUtm &&
          signupLocaleByPrefix &&
          !newUrl.searchParams.has("locale")
        ) {
          const [, prefix = ""] = window.location.pathname.split("/");
          const signupLocale = signupLocaleByPrefix[prefix];
          if (signupLocale) {
            newUrl.searchParams.set("locale", signupLocale);
            modified = true;
          }
        }
      }

      if (modified) {
        link.href = newUrl.toString();
      }
    } catch {
      return;
    }
  });

  useEffect(() => {
    const handleEvent = (e: Event) => decorateLink(e.target as Element);

    // pointerdown fires before click / auxclick / contextmenu for every button,
    // so middle-click and right-click ("open in new tab", "copy link address")
    // get the href decorated before the browser/OS reads it. click is kept for
    // keyboard activation (Enter emits no pointer event); contextmenu is a
    // safeguard. All listeners only mutate href — never preventDefault — to stay
    // race-free with the internal-link click interceptors.
    document.addEventListener("pointerdown", handleEvent, true);
    document.addEventListener("click", handleEvent, true);
    document.addEventListener("contextmenu", handleEvent, true);

    return () => {
      document.removeEventListener("pointerdown", handleEvent, true);
      document.removeEventListener("click", handleEvent, true);
      document.removeEventListener("contextmenu", handleEvent, true);
    };
  }, []);

  return null;
}
